import { Color3, Mesh, MeshBuilder, StandardMaterial, Vector3 } from '@babylonjs/core';
import { AssetObject } from '../AssetObject';
import { EnemyDistributionConfig } from '../EnemyGenerator';
import { Level } from '../Level';
import { AssetObjectConfig, MeshFactory } from '../MeshFactory';
import { PlayerCollision } from '../PlayerCollision';
import { ENEMY_ANIMATIONS, EnemyBehavior } from './EnemyBehavior';

interface SnipeProjectile {
  mesh: Mesh;
  velocity: Vector3;
  lifeTime: number;
}

/**
 * Snipe Behavior – stationary enemy that shows an aim laser then fires.
 *
 * Config fields:
 *   snipeRange     – detection/fire range (default 35)
 *   snipeInterval  – aim duration before firing (default 2.5)
 *   speed          – projectile speed (default 20)
 *   fireInterval   – cooldown between shots (default 5)
 */
export class EnemySnipeBehavior extends EnemyBehavior {
  private state: 'idle' | 'aiming' | 'cooldown' = 'idle';
  private aimTimer: number = 0;
  private cooldownTimer: number = 0;
  private aimLaser: Mesh | null = null;
  private projectiles: SnipeProjectile[] = [];

  constructor(startPosition: Vector3, config: EnemyDistributionConfig) {
    super('enemySnipe', startPosition, config);
  }

  initLevel(level: Level): EnemySnipeBehavior {
    super.initLevel(level);
    return this;
  }

  behaviorUpdate(deltaTime: number): void {
    if (!this.level?.player) return;

    const range = this.config.snipeRange ?? 25;
    const aimDuration = this.config.snipeInterval ?? 2.5;
    const cooldown = this.config.snipeInterval ? this.config.snipeInterval * 2 : 5;
    const dist = this.getPlayerDistance();

    this.updateProjectiles(deltaTime);

    switch (this.state) {
      case 'idle': {
        this.playAnimation(ENEMY_ANIMATIONS.IDLE, true);
        this.snapToGround(deltaTime);
        if (dist <= range) {
          this.state = 'aiming';
          this.aimTimer = 0;
          this.showAimLaser();
        } else {
          this.lookAt(this.getPlayerPosition());
        }
        break;
      }
      case 'aiming': {
        this.playAnimation(ENEMY_ANIMATIONS.CROUCH, true);
        this.lookAt(this.getPlayerPosition());
        this.updateAimLaser();
        this.aimTimer += deltaTime;

        // Make laser pulse by toggling visibility rapidly
        if (this.aimLaser) {
          this.aimLaser.isVisible = Math.sin(this.aimTimer * 20) > 0;
        }

        if (this.aimTimer >= aimDuration) {
          this.hideAimLaser();
          this.fire();
          this.state = 'cooldown';
          this.cooldownTimer = 0;
        }
        break;
      }
      case 'cooldown': {
        this.playAnimation(ENEMY_ANIMATIONS.IDLE, true);
        this.snapToGround(deltaTime);
        this.cooldownTimer += deltaTime;
        if (this.cooldownTimer >= cooldown) {
          this.state = dist <= range ? 'aiming' : 'idle';
          this.aimTimer = 0;
        }
        break;
      }
    }
  }

  private showAimLaser(): void {
    if (!this.level) return;
    this.aimLaser = MeshBuilder.CreateCylinder('snipeAimLaser', { height: 1, diameter: 0.05, tessellation: 5 }, this.level.scene);
    const mat = new StandardMaterial('snipeLaserMat', this.level.scene);
    mat.emissiveColor = new Color3(1, 0, 0);
    mat.disableLighting = true;
    mat.alpha = 0.4;
    this.aimLaser.material = mat;
    this.updateAimLaser();
  }

  private updateAimLaser(): void {
    if (!this.aimLaser || !this.level?.player) return;
    const origin = this.mesh.position.clone();
    origin.y += 1.2;
    const target = this.getPlayerPosition();
    const dir = target.subtract(origin);
    const len = dir.length();

    this.aimLaser.scaling.y = len;
    const mid = origin.add(dir.scale(0.5));
    this.aimLaser.position.copyFrom(mid);

    // Orient: the cylinder's local Y is the axis
    const normalized = dir.normalize();
    const dot = Vector3.Dot(Vector3.Up(), normalized);
    const cross = Vector3.Cross(Vector3.Up(), normalized);
    if (cross.length() > 0.001) {
      const angle = Math.acos(Math.min(1, Math.max(-1, dot)));
      this.aimLaser.rotationQuaternion = null;
      this.aimLaser.rotation = Vector3.Zero();
      const axis = cross.normalize();
      this.aimLaser.rotate(axis, angle);
    }
  }

  private hideAimLaser(): void {
    if (this.aimLaser) {
      this.aimLaser.material?.dispose();
      this.aimLaser.dispose();
      this.aimLaser = null;
    }
  }

  private fire(): void {
    if (!this.level) return;
    this.playAnimation(ENEMY_ANIMATIONS.ATTACK_MELEE_RIGHT, false);

    const spawnPos = this.mesh.position.clone();
    spawnPos.y += 1.2;
    const dir = this.getPlayerPosition().subtract(spawnPos).normalize();
    const speed = this.config.speed ?? 10;

    const bombAsset = new AssetObject('obstacleProjectile').withClone(true);
    const cfg = AssetObjectConfig.builder().object(bombAsset).scale(1).physicsShapeType(undefined).build();
    const projectileMesh = cfg.buildMesh(this.level) as Mesh;
    MeshFactory.configureMesh(projectileMesh, cfg, this.level);
    projectileMesh.position.copyFrom(spawnPos);

    const p: SnipeProjectile = {
      mesh: projectileMesh,
      velocity: dir.scale(speed),
      lifeTime: 8,
    };

    projectileMesh.metadata = {
      ...projectileMesh.metadata,
      playerCollision: new PlayerCollision(false, (level, player, event) => {
        if (!player.vulnerable || level.game.config.godMode) {
          this.removeProjectile(this.projectiles.indexOf(p));
        } else {
          player.collide(event, 'Hit by sniper!');
          this.removeProjectile(this.projectiles.indexOf(p));
        }
      }),
    };
    this.level.player?.registerCollisionMesh(projectileMesh);
    this.level.game.sound.play('explosion', this.mesh);
    this.projectiles.push(p);
  }

  private updateProjectiles(deltaTime: number): void {
    for (let i = this.projectiles.length - 1; i >= 0; i--) {
      const p = this.projectiles[i];
      p.lifeTime -= deltaTime;
      if (p.lifeTime <= 0) {
        this.removeProjectile(i);
        continue;
      }
      p.mesh.position.addInPlace(p.velocity.scale(deltaTime));
    }
  }

  private removeProjectile(index: number): void {
    if (index < 0 || index >= this.projectiles.length) return;
    const p = this.projectiles[index];
    this.level?.player?.unregisterCollisionMesh(p.mesh);
    this.level?.particles.createDisappearanceParticleSystem(p.mesh, 0.3);
    p.mesh.dispose();
    this.projectiles.splice(index, 1);
  }

  detach(): void {
    this.hideAimLaser();
    for (const p of this.projectiles) {
      this.level?.player?.unregisterCollisionMesh(p.mesh);
      p.mesh.dispose();
    }
    this.projectiles = [];
    super.detach();
  }

  clone(): EnemySnipeBehavior {
    return new EnemySnipeBehavior(this.startPosition, this.config);
  }
}
